import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FaFile,
  FaFileArchive,
  FaFileAudio,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFileVideo,
  FaFileWord,
  FaRegTrashAlt,
} from 'react-icons/fa';
import { FaUsers } from 'react-icons/fa';
import { LuForward } from 'react-icons/lu';
import { BsPaperclip, BsThreeDotsVertical } from 'react-icons/bs';
import { LiaGoogleDrive } from 'react-icons/lia';
import toast from 'react-hot-toast';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';

import {
  google_client_id,
  google_api_key,
  GOOGLE_DRIVE_PICKER_ENABLED,
} from '../../utils/keys';
import useDrivePicker from '../../hooks/useGoogleDrivePicker';
import { DataState } from '../../context/DataProvider';
import { forwardGmailEmail, forwardOutlookEmail } from '../../services/api';
import { draftForwardEmail } from '../../services/email_api';
import SunEditorComponent from './SunEditorComponent';

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf'))
    return <FaFilePdf className="text-red-500" size={20} />;
  if (
    mimeType?.includes('word') ||
    mimeType?.includes('officedocument.wordprocessingml')
  )
    return <FaFileWord className="text-[#5652CF]" size={20} />;
  if (
    mimeType?.includes('excel') ||
    mimeType?.includes('officedocument.spreadsheetml')
  )
    return <FaFileExcel className="text-green-500" size={20} />;
  if (mimeType?.includes('image'))
    return <FaFileImage className="text-yellow-500" size={20} />;
  if (
    mimeType?.includes('zip') ||
    mimeType?.includes('rar') ||
    mimeType?.includes('archive')
  )
    return <FaFileArchive className="text-zinc-400" size={20} />;
  if (mimeType?.includes('audio'))
    return <FaFileAudio className="text-purple-500" size={20} />;
  if (mimeType?.includes('video'))
    return <FaFileVideo className="text-indigo-500" size={20} />;
  return <FaFile className="text-zinc-400" size={20} />;
};

const AttachmentItem = ({ file, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async () => {
    if (isDownloading || file.from !== 'local') return;
    setIsDownloading(true);

    try {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="inline-flex h-10 w-40 cursor-pointer items-center justify-center rounded-lg border border-zinc-700 p-2 py-3 text-zinc-200 shadow-md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && file.from === 'local' ? (
        <div className="flex w-full items-center justify-center gap-4">
          {isDownloading ? (
            <AiOutlineLoading3Quarters
              className="animate-spin text-zinc-400"
              size={16}
            />
          ) : (
            <FiDownload
              onClick={handleDownloadClick}
              className="cursor-pointer text-zinc-400 hover:text-black"
              size={16}
            />
          )}
          <button
            onClick={() => onRemove(file)}
            className="ml-2 text-zinc-400 hover:text-red-400"
          >
            &times;
          </button>
        </div>
      ) : (
        <div className="flex w-full items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center">
            {getFileIcon(file?.file.mimeType || file?.file.type)}
          </div>
          <span className="truncate text-sm font-medium">
            {file?.file.filename || file?.file.name}
          </span>
          {hovered && (
            <button
              onClick={() => onRemove(file)}
              className="ml-auto text-zinc-400 hover:text-red-400"
            >
              &times;
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const BriefingForwardEmail = ({
  message_id,
  setShowForwardPanel,
  message,
  setMessage,
  recepientsEmails = [],
  setRecepientsEmails,
  inputValue = '',
  setInputValue,
  isOutlook,
}) => {
  const fileInputRef = useRef(null);
  const { userGoogleId } = DataState();
  const [loading, setLoading] = useState(false);
  const [ccs, setCcs] = useState([]);
  const [bccs, setBccs] = useState([]);
  const [inputCcValue, setInputCcValue] = useState('');
  const [inputBccValue, setInputBccValue] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const [localAttachments, setLocalAttachments] = useState([]);
  const [driveAttachments, setDriveAttachments] = useState([]);

  // New state to handle just updated content from AI or other sources
  const [justUpdatedValue, setJustUpdatedValue] = useState('');

  const [customPrompt, setCustomPrompt] = useState('');
  const [customPromptLoading, setCustomPromptLoading] = useState(false);

  // Corrected the use of the useDrivePicker hook
  const [openPicker] = useDrivePicker();

  const allAttachments = useMemo(() => {
    const local = localAttachments.map((file) => ({
      name: file.name,
      from: 'local',
      file,
    }));
    const drive = driveAttachments.map((file) => ({
      name: file.name,
      from: 'drive',
      id: file.id,
      file,
    }));
    return [...local, ...drive];
  }, [localAttachments, driveAttachments]);

  // Use useEffect to update the message state whenever originalEmailBody changes.
  // This ensures the editor is pre-populated and updates correctly.
  useEffect(() => {
    if (message) {
      setMessage(message);
      setJustUpdatedValue(message);
    }
    // run only once when mounting
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (event, type) => {
    const value = (
      type === 'to' ? inputValue : type === 'cc' ? inputCcValue : inputBccValue
    )
      .trim()
      .replace(/,$/, '');
    if (
      (event.key === 'Enter' || event.key === ',' || event.key === ' ') &&
      value
    ) {
      event.preventDefault();
      const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      const match = value.match(emailPattern);

      if (match) {
        if (type === 'to') {
          if (!recepientsEmails.includes(value))
            setRecepientsEmails((prev) => [...prev, value]);
          setInputValue('');
        } else if (type === 'cc') {
          if (!ccs.includes(value)) setCcs((prev) => [...prev, value]);
          setInputCcValue('');
        } else if (type === 'bcc') {
          if (!bccs.includes(value)) setBccs((prev) => [...prev, value]);
          setInputBccValue('');
        }
      } else {
        toast.error('Invalid email format!');
      }
    }
  };

  const removeEmail = (emailToRemove, type) => {
    if (type === 'to') {
      setRecepientsEmails((prev) =>
        prev.filter((email) => email !== emailToRemove),
      );
    } else if (type === 'cc') {
      setCcs((prev) => prev.filter((email) => email !== emailToRemove));
    } else if (type === 'bcc') {
      setBccs((prev) => prev.filter((email) => email !== emailToRemove));
    }
  };

  const handleDraftForward = async () => {
    if (!customPrompt.trim()) {
      toast.error('Please provide a prompt to generate a draft.');
      return;
    }
    setCustomPromptLoading(true);
    try {
      const response = await draftForwardEmail(
        userGoogleId,
        message_id,
        customPrompt,
        isOutlook,
      );

      const htmlBody = response?.html_body || '';

      setMessage(htmlBody);
      setJustUpdatedValue(htmlBody);
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setCustomPromptLoading(false);
    }
  };

  const handleForward = async () => {
    setLoading(true);

    if (!recepientsEmails.length) {
      toast.error('Please enter at least one recipient.');
      setLoading(false);
      return;
    }

    const forwardData = {
      recipients: recepientsEmails,
      ccs: ccs,
      bccs: bccs,
      message_id: message_id,
      comment: message,
    };

    try {
      if (isOutlook) {
        await forwardOutlookEmail(userGoogleId, forwardData);
      } else {
        await forwardGmailEmail(userGoogleId, forwardData, isOutlook);
      }
      toast.success('Email forwarded successfully!');
      setRecepientsEmails([]);
      setInputValue('');
      setMessage('');
      setJustUpdatedValue('clearing');
      setJustUpdatedValue('');
    } catch (error) {
      toast.error('Failed to forward email.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrashClick = () => {
    setMessage('');
    setCustomPrompt('');
    setJustUpdatedValue('clearing');
    setJustUpdatedValue('');
    setShowForwardPanel(false);
    setRecepientsEmails([]);
    setLocalAttachments([]);
    setDriveAttachments([]);
  };

  const handlePaperClipClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setLocalAttachments((prevAttachments) => [...prevAttachments, ...files]);
    event.target.value = null;
  };

  const handleRemove = (fileToRemove) => {
    if (fileToRemove.from === 'local') {
      setLocalAttachments((prev) =>
        prev.filter((file) => file.name !== fileToRemove.name),
      );
    } else if (fileToRemove.from === 'drive') {
      setDriveAttachments((prev) =>
        prev.filter((file) => file.id !== fileToRemove.id),
      );
    }
  };

  const handleOpenPicker = () => {
    openPicker({
      clientId: google_client_id,
      developerKey: google_api_key,
      viewId: 'DOCS',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      callbackFunction: (data) => {
        if (data.action === 'picked') {
          const files = data.docs.map((file) => ({
            id: file.id,
            name: file.name,
            mimeType: file?.mimeType || file?.type,
            url: file.url,
          }));
          setDriveAttachments((prev) => [...prev, ...files]);
        }
      },
    });
  };

  return (
    <div className="flex w-full flex-col p-5 transition duration-200">
      <div className="mb-4 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
        <LuForward className="h-4 w-4" />
        <span>Forward</span>
      </div>
      <div className="flex w-full flex-col">
        <div className="w-full">
          <div className="mb-3 flex w-full flex-row items-start gap-2 text-zinc-300">
            <FaUsers className="mt-2 hidden h-5 w-5 self-center text-zinc-400 sm:block" />
            <div className="flex min-h-[36px] w-full flex-grow flex-wrap items-center gap-1 rounded-md border border-zinc-700/50 bg-zinc-900/40 p-1.5 text-xs text-zinc-300">
              {recepientsEmails.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded bg-zinc-700/50 px-2 py-1 text-zinc-200"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, 'to')}
                    className="ml-2 text-zinc-400 hover:text-red-400"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="min-w-[120px] flex-grow bg-transparent text-zinc-300 outline-none"
                placeholder="Enter recipients..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'to')}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCc(!showCc)}
                className={`flex min-h-[36px] items-center justify-center rounded-md border border-zinc-700/50 px-2 py-1 text-xs transition-colors duration-200 hover:bg-zinc-700/50 ${showCc ? 'bg-zinc-700/50' : 'bg-transparent'} w-full text-center sm:w-auto`}
              >
                Cc
              </button>
              <button
                onClick={() => setShowBcc(!showBcc)}
                className={`flex min-h-[36px] items-center justify-center rounded-md border border-zinc-700/50 px-2 py-1 text-xs transition-colors duration-200 hover:bg-zinc-700/50 ${showBcc ? 'bg-zinc-700/50' : 'bg-transparent'} w-full text-center sm:w-auto`}
              >
                Bcc
              </button>
            </div>
          </div>

          {showCc && (
            <div className="mb-3 flex min-h-[36px] w-full flex-wrap items-center gap-1 rounded-md border border-zinc-700/50 bg-zinc-900/40 p-1.5 text-xs">
              {ccs.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded bg-zinc-700/50 px-2 py-1 text-zinc-200"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, 'cc')}
                    className="ml-2 text-zinc-400 hover:text-red-400"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="min-w-[120px] flex-grow bg-transparent text-zinc-300 outline-none"
                placeholder="Cc..."
                value={inputCcValue}
                onChange={(e) => setInputCcValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'cc')}
              />
            </div>
          )}

          {showBcc && (
            <div className="mb-3 flex min-h-[36px] w-full flex-wrap items-center gap-1 rounded-md border border-zinc-700/50 bg-zinc-900/40 p-1.5 text-xs">
              {bccs.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded bg-zinc-700/50 px-2 py-1 text-zinc-200"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, 'bcc')}
                    className="ml-2 text-zinc-400 hover:text-red-400"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="min-w-[120px] flex-grow bg-transparent text-zinc-300 outline-none"
                placeholder="Bcc..."
                value={inputBccValue}
                onChange={(e) => setInputBccValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'bcc')}
              />
            </div>
          )}

          <SunEditorComponent
            value={message}
            onChange={setMessage}
            justUpdatedValue={justUpdatedValue}
          />

          {allAttachments.length > 0 && (
            <div className="mt-2 flex flex-grow flex-wrap items-center gap-2 p-1">
              {allAttachments.map((file, index) => (
                <AttachmentItem
                  key={index}
                  file={file}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-col items-center justify-between text-zinc-200">
            <div className="flex w-full flex-col items-stretch justify-between space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
              <input
                type="text"
                name="custom prompt"
                value={customPrompt}
                placeholder="Add prompt for AI-generated forward draft..."
                onChange={(e) => setCustomPrompt(e.target.value)}
                className="w-full rounded-md border border-zinc-700/50 bg-zinc-900/40 p-2 text-xs text-zinc-300 focus:border-[#5652CF] focus:outline-none"
              />
              <button
                onClick={handleDraftForward}
                disabled={customPromptLoading}
                className="flex w-full items-center justify-center space-x-2 rounded bg-zinc-700/50 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-600/50 sm:w-auto"
              >
                <img src="/assets/logo.png" alt="Logo" className="h-4 w-4" />
                {customPromptLoading ? (
                  <span>Generating...</span>
                ) : (
                  <span>Generate</span>
                )}
              </button>
            </div>

            <div className="flex w-full items-center justify-between gap-2 space-x-3 p-2 pl-0 pr-0 sm:gap-0">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleForward}
                  disabled={loading || recepientsEmails.length === 0}
                  className="flex items-center justify-between rounded bg-[#5652CF] px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-600"
                >
                  <div className="flex h-full items-center justify-center pl-1">
                    <LuForward size={16} />
                  </div>
                  <div className="pl-2 pr-2">
                    {loading ? 'Forwarding...' : 'Forward'}
                  </div>
                </button>
                <div className="flex">
                  {/* eslint-disable-next-line */}
                  <div
                    onClick={handlePaperClipClick}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                  >
                    <BsPaperclip size={18} />
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                      multiple
                    />
                  </div>
                  {GOOGLE_DRIVE_PICKER_ENABLED && (
                    // eslint-disable-next-line
                    <div
                      onClick={handleOpenPicker}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                    >
                      <LiaGoogleDrive size={18} />
                    </div>
                  )}
                  <div className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100">
                    <BsThreeDotsVertical size={18} />
                  </div>
                </div>
              </div>
              <div className="ml-auto w-auto">
                <button
                  onClick={handleTrashClick}
                  className="text-zinc-400 transition-colors hover:text-zinc-100"
                >
                  <FaRegTrashAlt size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BriefingForwardEmail;
